/**
 * secure-storage.ts
 *
 * Reemplaza window.localStorage con un Proxy transparente que ofusca
 * tanto las claves como los valores usando Base64 + salt.
 *
 * El resto del código sigue usando localStorage.setItem / getItem normalmente.
 * Desde DevTools solo se ven strings ofuscados.
 *
 * Estrategia:
 *  - Clave  → btoa(SALT + ":" + rawKey)
 *  - Valor  → btoa(unescape(encodeURIComponent(rawValue)))   (soporta UTF-8)
 *
 * No es criptografía fuerte — el objetivo es impedir lectura/escritura
 * casual desde DevTools, no resistir un ataque deliberado.
 */

const SALT = "ds_v1";

// Claves que NO deben ofuscarse (usadas por librerías externas o DevTools internals)
const BYPASS_PREFIXES = ["__vite", "vite:", "HMR"];

function shouldBypass(key: string): boolean {
  return BYPASS_PREFIXES.some((p) => key.startsWith(p));
}

// ── Codificación ──────────────────────────────────────────────────────────────

function encodeKey(rawKey: string): string {
  if (shouldBypass(rawKey)) return rawKey;
  try {
    return btoa(`${SALT}:${rawKey}`);
  } catch {
    return rawKey;
  }
}

function decodeKey(encodedKey: string): string {
  if (shouldBypass(encodedKey)) return encodedKey;
  try {
    const decoded = atob(encodedKey);
    const sep = decoded.indexOf(":");
    if (sep !== -1 && decoded.slice(0, sep) === SALT) {
      return decoded.slice(sep + 1);
    }
    // No tiene el salt esperado → clave legacy sin ofuscar, devolver tal cual
    return encodedKey;
  } catch {
    return encodedKey;
  }
}

function encodeValue(rawValue: string): string {
  try {
    return btoa(unescape(encodeURIComponent(rawValue)));
  } catch {
    return rawValue;
  }
}

function decodeValue(encodedValue: string): string {
  try {
    return decodeURIComponent(escape(atob(encodedValue)));
  } catch {
    // Valor legacy sin codificar → devolver tal cual
    return encodedValue;
  }
}

// ── Migración de claves legacy ────────────────────────────────────────────────
// Si quedan entradas antiguas en texto plano de sesiones anteriores, las
// re-escribe ofuscadas y elimina las originales.

const KNOWN_KEYS = [
  "user", "role", "user_type", "auth_token", "auth_token_expiry",
  "client_discount", "cliente_id",
  "session_expiry_timestamp", "session_duration_seconds",
  "admin-dark-theme", "admin-sidebar-collapsed", "client-theme",
  "loginAttempts", "isBlocked", "serverUnlockTimestamp", "formattedUnlockTime",
  "limited_access", "pedidos_detalle_locales", "combo_detalles_cache_v1",
  "cached_cronogramas_online", "carrito", "med_cache_v1",
  "drosi-open", "home_per_page", "cart_tab", "products-search-history",
  "oferta_id_from_carousel", "oferta_seleccionada", "pedidos_ocultos",
];

function migrateLegacyKeys(store: Storage): void {
  // Migrar claves conocidas fijas
  for (const rawKey of KNOWN_KEYS) {
    const legacyValue = store.getItem(rawKey);
    if (legacyValue === null) continue;
    const encodedKey = encodeKey(rawKey);
    if (store.getItem(encodedKey) === null) {
      store.setItem(encodedKey, encodeValue(legacyValue));
    }
    store.removeItem(rawKey);
  }

  // Migrar claves con patrones dinámicos (popup_closed_*, etc.)
  const dynamicPrefixes = ["popup_closed_"];
  const keysToMigrate: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (k && dynamicPrefixes.some((p) => k.startsWith(p))) {
      keysToMigrate.push(k);
    }
  }
  for (const rawKey of keysToMigrate) {
    const legacyValue = store.getItem(rawKey);
    if (legacyValue === null) continue;
    const encodedKey = encodeKey(rawKey);
    if (store.getItem(encodedKey) === null) {
      store.setItem(encodedKey, encodeValue(legacyValue));
    }
    store.removeItem(rawKey);
  }
}

// ── Proxy ─────────────────────────────────────────────────────────────────────

function createSecureStorage(store: Storage): Storage {
  return new Proxy(store, {
    get(target, prop: string) {
      switch (prop) {
        case "setItem":
          return (key: string, value: string) => {
            target.setItem(encodeKey(key), encodeValue(value));
          };

        case "getItem":
          return (key: string): string | null => {
            if (shouldBypass(key)) return target.getItem(key);
            const encoded = target.getItem(encodeKey(key));
            if (encoded === null) return null;
            return decodeValue(encoded);
          };

        case "removeItem":
          return (key: string) => {
            target.removeItem(encodeKey(key));
          };

        case "clear":
          return () => target.clear();

        case "key":
          return (index: number): string | null => {
            const encodedKey = target.key(index);
            if (encodedKey === null) return null;
            return decodeKey(encodedKey);
          };

        case "length":
          return target.length;

        default:
          // Acceso directo por propiedad: ej. localStorage.role
          if (typeof prop === "string" && typeof (target as any)[prop] !== "function") {
            const encoded = target.getItem(encodeKey(prop));
            if (encoded !== null) return decodeValue(encoded);
          }
          const val = (target as any)[prop];
          return typeof val === "function" ? val.bind(target) : val;
      }
    },

    // ── Object.keys(localStorage) devuelve claves decodificadas ──
    ownKeys(target) {
      const seen = new Set<string>();
      for (let i = 0; i < target.length; i++) {
        const k = target.key(i);
        if (k !== null) seen.add(decodeKey(k));
      }
      return [...seen];
    },

    getOwnPropertyDescriptor(target, prop: string) {
      // Necesario para que ownKeys funcione con Object.keys()
      const encodedProp = encodeKey(prop);
      if (target.getItem(encodedProp) !== null || target.getItem(prop) !== null) {
        return { enumerable: true, configurable: true, writable: true, value: undefined };
      }
      return undefined;
    },

    set(target, prop: string, value: unknown) {
      if (prop === "setItem" || prop === "getItem" || prop === "removeItem") return false;
      (target as any)[prop] = value;
      return true;
    },
  });
}

// ── Instalación ───────────────────────────────────────────────────────────────

let installed = false;

export function installSecureStorage(): void {
  if (installed) return;
  installed = true;

  const native = window.localStorage;

  // 1. Migrar claves legacy antes de instalar el proxy
  migrateLegacyKeys(native);

  // 2. Reemplazar window.localStorage con el proxy
  try {
    Object.defineProperty(window, "localStorage", {
      value: createSecureStorage(native),
      writable: false,
      configurable: true,
    });
  } catch (e) {
    console.warn("[secure-storage] No se pudo instalar el proxy:", e);
  }
}
